import { Workbook, Worksheet } from 'exceljs';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { UserHlp } from 'src/lib/fliko/helpers/user.helper';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { ResidentType, ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { ParkingData } from 'src/lib/fliko/models/units/parking.data';
import { StorageData } from 'src/lib/fliko/models/units/storage.data';
import { RentalUnitType, UnitLocation } from 'src/lib/fliko/models/units/unit.data';
import { UnitCode } from 'src/lib/fliko/models/units/unitCode';
import { ExcelFileFactory } from 'src/app/shared/factories/excel-file.factory';
import { GrammarHelper } from 'src/app/shared/helpers/grammar.helper';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { NoteExportHlp } from 'src/app/shared/models/note-table.model';
import { NoteVM } from 'src/app/shared/models/notes.view-model';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { unitComparator } from '../helpers/unit-sort.helper';
import { CommunityReportColumns, CommonReport, CommunityReport } from '../services/models/table/users-report-table-model';
import { UserCommList } from '../services/models/users-table.data';
import { UnitResidentViewModel, UnitCommResidencyViewModel, TenancyViewModel } from '../services/models/users-view-models';

interface UnitSheetVM {
  name: string;
  sheet: Worksheet;

  shareColumn: string;
  percentColumn: string;

  lastDataRow: number;
  firstSumRow: number;
}

export class CommApartmentExportReport {
  private landlordExportData: CommunityReport.LandlordData[] = [];
  private tenantExportData: CommunityReport.TenantData[] = [];
  private apartmentsExportData: CommunityReport.ApartmentData[] = [];
  private parkingsExportData: CommunityReport.ParkingStorageData[] = [];
  private storagesExportData: CommunityReport.ParkingStorageData[] = [];
  private tenanciesExportData: CommunityReport.TenancyData[] = [];
  private landlordExportNotes: CommonReport.ResidentNote[] = [];
  private tenantExportNotes: CommonReport.ResidentNote[] = [];

  private readonly locHelper = new LocalizationHelper(this.ls);

  constructor(
    private ls: LocalizationService,
    private estate: EstateData,
    private apartments: UnitCommResidencyViewModel[] = [],
    private parkings: UnitCommResidencyViewModel[] = [],
    private storages: UnitCommResidencyViewModel[] = [],
    private landlords: UserCommList.ResidentElement[] = [],
    private tenants: UserCommList.ResidentElement[] = [],
    private tenancies: TenancyViewModel[] = [],
    private notes = new Map<string, NoteVM<ResidentData>[]>()
  ) {}

  public generateRaport(tenantsEnabled: boolean) {
    const t = (x: string) => this.ls.translate(x);
    this.prepareData(tenantsEnabled);

    const eg = new ExcelFileFactory();
    const hOpt = { height: 50 };

    eg.addSheet(t('commLandlords.nominative'), CommunityReportColumns.landlords, this.landlordExportData, this.ls, hOpt);
    if (tenantsEnabled) {
      eg.addSheet(t('tenants.nominative'), CommunityReportColumns.tenants, this.tenantExportData, this.ls, hOpt);
    }

    eg.addSheet(t('commApartment.plural'), CommunityReportColumns.apartments(), this.apartmentsExportData, this.ls, hOpt);

    if (this.parkingsExportData.length) {
      eg.addSheet(t('parking.plural'), CommunityReportColumns.parkings(), this.parkingsExportData, this.ls, hOpt);
    }
    if (this.storagesExportData.length) {
      eg.addSheet(t('storage.plural'), CommunityReportColumns.storages(), this.storagesExportData, this.ls, hOpt);
    }

    this.addOtherSharesSum(eg.getWorkbook());

    if (tenantsEnabled && this.tenanciesExportData.length) {
      eg.addSheet(t('rUser.tenAccess'), CommunityReportColumns.tenancies, this.tenanciesExportData, this.ls, hOpt);
    }

    const lNotes = eg.addSheet(
      `${t('commLandlords.nominative')} - ${t('label.notes')}`,
      CommonReport.residentsNotesColumns,
      this.landlordExportNotes,
      this.ls,
      hOpt
    );
    NoteExportHlp.format(eg, lNotes, ['B'], ['E', 'F']);

    if (tenantsEnabled) {
      const tNotes = eg.addSheet(
        `${t('tenants.nominative')} - ${t('label.notes')}`,
        CommonReport.residentsNotesColumns,
        this.tenantExportNotes,
        this.ls,
        hOpt
      );
      NoteExportHlp.format(eg, tNotes, ['B'], ['E', 'F']);
    }

    eg.saveToFile(
      `${StrHlp.capitalize(t('label.report'))}_${t('rUser.resNoPolish')}_${this.locHelper.localizeDate(Date.now(), 'dShort')}`
    );
  }

  private prepareData(tenantsEnabled: boolean) {
    this.apartmentsExportData = [];
    this.parkingsExportData = [];
    this.storagesExportData = [];
    this.landlordExportData = [];
    this.tenantExportData = [];
    this.tenanciesExportData = [];
    this.landlordExportNotes = [];
    this.tenantExportNotes = [];

    const fd = (x: Date | number | null | undefined, empty?: string) => this.locHelper.localizeDate(x, 'dShort', empty);

    this.apartments.forEach((aVM) => {
      const apartment = aVM.unit as ApartmentData & { landAndMortgageRegister?: string };
      if (apartment) {
        const residentsNames = this.getLandlordNames(aVM.residents);
        const parkings = this.parkings
          .filter((p) => (p.unit as ParkingData).apartmentId == apartment.id)
          .map((p) => p.unitName)
          .join(', ');
        const storages = this.storages
          .filter((s) => (s.unit as StorageData).apartmentId == apartment.id)
          .map((s) => s.unitName)
          .join(',');
        this.apartmentsExportData.push({
          id: '',
          blockNumber: apartment.blockNumber,
          street: apartment.street || '',
          staircaseNumber: apartment.staircaseNumber,
          floor: apartment.floor,
          number: apartment.number,
          rooms: apartment.roomsCount?.toString() ?? '',
          residentsNames,
          landRegister: apartment.landRegister ?? apartment.landAndMortgageRegister ?? '',
          sharesInEstate: apartment.shareInEstate ?? 0,
          sharesPercent: this.getSharePercent(apartment.shareInEstate),
          area: apartment.area ?? 0,
          unitCodes: UnitCode.allFromUnit(apartment).join(', '),
          parkingNumbers: parkings,
          storageNumbers: storages,
          bankAccountNumber: this.formatBankAccount(aVM.bankAccount)
        });
      }
    });
    // ? Sort apartment data by block / number and update ids
    this.apartmentsExportData.sort(unitComparator);
    this.apartmentsExportData.forEach((data, index) => (data.id = `#${index + 1}`));

    this.parkings.forEach((pVM) => {
      // ? omit parkings without shares
      if ((pVM.unit as ParkingData).apartmentId) return;
      this.parkingsExportData.push(this.handleParkingStorage(this.parkingsExportData.length, pVM, RentalUnitType.PARKING));
    });
    // ? Sort parkings by block/number
    this.parkingsExportData.sort(unitComparator);
    this.parkingsExportData.forEach((data, index) => (data.id = `#${index + 1}`));

    this.storages.forEach((sVM) => {
      // ? omit storages without shares
      if ((sVM.unit as StorageData).apartmentId) return;
      this.storagesExportData.push(this.handleParkingStorage(this.storagesExportData.length, sVM, RentalUnitType.STORAGE));
    });
    // ? Sort storages by block/number
    this.storagesExportData.sort(unitComparator);
    this.storagesExportData.forEach((data, index) => (data.id = `#${index + 1}`));

    this.landlords.forEach((element) => {
      this.landlordExportData.push(
        this.handleResident(this.landlordExportData.length, element, ResidentType.LANDLORD) as CommunityReport.LandlordData
      );
      const notes = this.notes.get(element.id);
      if (notes?.length) this.landlordExportNotes.push(...this.handleResidentNotes(notes));
    });

    if (tenantsEnabled) {
      this.tenants.forEach((element) => {
        this.tenantExportData.push(this.handleResident(this.tenantExportData.length, element, ResidentType.TENANT));

        const notes = this.notes.get(element.id);
        if (notes?.length) this.tenantExportNotes.push(...this.handleResidentNotes(notes));
      });

      this.tenancies.forEach((element) => {
        if (!UserHlp.isCurrentCommunityTenancy(element)) return;

        if (element.apartment) {
          this.tenanciesExportData.push({
            id: `#${this.tenanciesExportData.length + 1}`,
            landlordName: ToString.fullName(element.landlord),
            tenantName: ToString.fullName(element.tenant),
            tenancyStart: fd(element.startDate),
            tenancyEnd: fd(element.endDate, this.ls.translate('label.infinite')),
            creationDate: fd(element.creationDate),
            creatorName: element.creatorUserName,
            apartmentNumber: ToString.shortNumber(element.apartment),
            parkingNumbers: this.getUnitsNumbers(element.parkings),
            storageNumbers: this.getUnitsNumbers(element.storages)
          });
        }
      });
    }
  }

  private getLandlordNames(residents: UnitResidentViewModel[]): string {
    return (residents ?? [])
      .filter((r) => r.type == ResidentType.LANDLORD)
      .map((r) => r.name)
      .join(', ');
  }

  private getUnitsNumbers(units: UnitLocation[]): string {
    return units.map((u) => ToString.longNumber(u)).join(', ');
  }

  private formatBankAccount(bankAccount: string | null): string {
    return bankAccount ? ParserHlp.parseIbanDisplay(bankAccount) : this.ls.translate('label.empty');
  }

  private getSharePercent(shares: number | undefined): number {
    return (shares ?? 0) / (this.estate.totalShares ?? 1);
  }

  // HACK: unresolved issue from 2017: https://github.com/exceljs/exceljs/issues/315
  private addOtherSharesSum(workbook: Workbook): void {
    const types = Object.values(RentalUnitType);

    for (const type of types) {
      const vm = this.getSheetVM(workbook, type);
      if (!vm) continue;

      vm.sheet.addRow({
        landRegister: this.ls.translate(`rUser.${type}Share`),
        sharesInEstate: { formula: `SUM(${vm.shareColumn}2:${vm.shareColumn}${vm.lastDataRow})` },
        sharesPercent: { formula: `SUM(${vm.percentColumn}2:${vm.percentColumn}${vm.lastDataRow})` }
      });

      let otherTypesCounter = 0;
      for (const other of types) {
        if (other == type) continue;

        const otherVm = this.getSheetVM(workbook, other);
        if (!otherVm) continue;

        vm.sheet.addRow({
          landRegister: this.ls.translate(`rUser.${other}Share`),
          sharesInEstate: { formula: `${otherVm.name}!${otherVm.shareColumn}${otherVm.firstSumRow}` },
          sharesPercent: { formula: `${otherVm.name}!${otherVm.percentColumn}${otherVm.firstSumRow}` }
        });
        otherTypesCounter++;
      }

      if (otherTypesCounter) {
        const lastSumRow = vm.firstSumRow + otherTypesCounter;

        vm.sheet.addRow({
          sharesInEstate: { formula: `SUM(${vm.shareColumn}${vm.firstSumRow}:${vm.shareColumn}${lastSumRow})` },
          sharesPercent: { formula: `SUM(${vm.percentColumn}${vm.firstSumRow}:${vm.percentColumn}${lastSumRow})` }
        });
      }
    }
  }

  private getSheetVM(workbook: Workbook, type: RentalUnitType): UnitSheetVM | null {
    const name = this.ls.translate(type == 'apartment' ? 'commApartment.plural' : `${type}.plural`);
    const sheet = workbook.getWorksheet(name);

    if (!sheet) return null;

    const rows = this[`${type}sExportData`].length;

    return {
      name,
      sheet,
      shareColumn: this.getShareColumnLetter(sheet, 'sharesInEstate'),
      percentColumn: this.getShareColumnLetter(sheet, 'sharesPercent'),
      lastDataRow: rows + 1,
      firstSumRow: rows + 2
    };
  }

  private getShareColumnLetter(sheet: Worksheet, key: 'sharesInEstate' | 'sharesPercent'): string {
    const charCodeOffset = 64; // One char before 'A'
    const column = sheet.getColumn(key);
    return String.fromCharCode(charCodeOffset + column.number);
  }

  private handleResident(
    id: number,
    element: UserCommList.ResidentElement,
    type: ResidentType
  ): CommunityReport.LandlordData | CommunityReport.TenantData {
    const resident = element.resident;
    const apartmentNumbers = this.getUnitsNumbers(element.apartments);
    const parkingNumbers = this.getUnitsNumbers(element.parkings);
    const storageNumbers = this.getUnitsNumbers(element.storages);

    const mapApartments = (key: keyof ApartmentData) =>
      element.apartments
        .map((a: ApartmentData & { landAndMortgageRegister?: string }) =>
          key === 'landRegister' ? (a[key] ?? a['landAndMortgageRegister'] ?? '') : String(a[key] ?? '').trim()
        )
        .filter((a) => !!a.length)
        .join(', ');

    const addressNumbers = {
      street: mapApartments('street'),
      blockNumber: mapApartments('blockNumber'),
      staircaseNumber: mapApartments('staircaseNumber'),
      floor: mapApartments('floor'),
      number: mapApartments('number')
    };
    const areaInSquareMeters = mapApartments('area');
    const landRegister = mapApartments('landRegister');

    let totShares = 0;
    let apShares = 0;
    let parShares = 0;
    let stoShares = 0;

    element.shares?.forEach((s) => {
      switch (s.unitType) {
        case RentalUnitType.APARTMENT:
          apShares += s.shareCount;
          break;
        case RentalUnitType.PARKING:
          parShares += s.shareCount;
          break;
        case RentalUnitType.STORAGE:
          stoShares += s.shareCount;
          break;
      }
      totShares += s.shareCount;
    });

    const landlordData = {
      totShares,
      totSharesPercent: this.getSharePercent(totShares),
      apShares,
      apSharesPercent: this.getSharePercent(apShares),
      parShares,
      parSharesPercent: this.getSharePercent(parShares),
      stoShares,
      stoSharesPercent: this.getSharePercent(stoShares),
      ownerCode: resident.ownerCode ?? ''
    };

    return {
      id: `#${id + 1}`,
      nameSurname: ToString.fullName(resident),
      apartmentNumbers,
      ...addressNumbers,
      areaInSquareMeters,
      landRegister,
      parkingNumbers,
      phone: StrHlp.join(resident.phone),
      email: StrHlp.join(resident.email),
      storageNumbers,
      address: this.locHelper.addressToString(resident.address),
      birthDate: this.locHelper.localizeDate(resident.birthDate, 'dShort'),
      documentNo: resident.documentNo ?? '',
      PESEL: resident.PESEL ?? '',
      nationality: resident.nationality ?? '',
      emergencyContact: ToString.fullNameContact(resident.emergencyContact),
      lastUpdate: this.locHelper.localizeDate(resident.lastUpdate, 'dtShort', this.ls.translate('label.never')),
      ...(type == ResidentType.LANDLORD ? landlordData : {})
    };
  }

  private handleResidentNotes(vms: NoteVM<ResidentData>[]): CommonReport.ResidentNote[] {
    return vms.map((vm, i) => {
      return { name: !i ? ToString.fullName(vm.context) : null, ...NoteExportHlp.createRow(vm, i, this.ls) };
    });
  }

  private handleParkingStorage(
    id: number,
    element: UnitCommResidencyViewModel,
    type: RentalUnitType
  ): CommunityReport.ParkingStorageData {
    const unit = element.unit;
    const residentsNames = this.getLandlordNames(element.residents);
    return {
      id: `#${id + 1}`,
      areaInSquareMeters: element.area ?? 0,
      bankAccountNumber: this.formatBankAccount(element.bankAccount),
      street: unit.street ?? '',
      blockNumber: unit.blockNumber ?? '',
      floor: unit.floor ?? '',
      landRegister: (unit as ParkingData).landRegister ?? '',
      unitCodes: UnitCode.allFromUnit(unit).join(', '),
      number: unit.number,
      residentsNames,
      sharesInEstate: unit.shareInEstate ?? 0,
      sharesPercent: this.getSharePercent(unit.shareInEstate),
      staircaseNumber: '',
      type:
        type == RentalUnitType.PARKING
          ? GrammarHelper.parkingTypeHR((unit as ParkingData).type, this.ls)
          : GrammarHelper.storageTypeHR((unit as StorageData).type, this.ls)
    };
  }
}
